import React, { useEffect } from "react";
import { useIntl } from "react-intl";
import { ErrorProvider, MfaSetupContext, ProfileContext } from "@app/provider";
import { FACTORS_TO_SHOW, Factor } from "@app/service/user";
import { useErrorController } from "@natera/platform/lib/hooks";
import { Button } from "@natera/form";
import SpinnerView from "@app/components/spinnerView";
import VerifyCodeFormStepper from "../verifyCodeFormStepper";
import { Profile } from "@app/provider/profile";
import FactorList from "@app/components/factorList/factorList";
import { HeapEventLocation } from "@app/provider/types";
import { FactorsObject } from "@app/provider/mfaSetup";
import { factorMessages as messages } from "./factorMessages";
import { setCodeVerificationErrors } from "@app/provider/mfaError";

import "./factorContainer.scss";

interface Props {
  profile?: Profile;
  isTokenizedFlow: boolean;
}

const FactorContainerSignUp: React.FunctionComponent<Props> = ({
  profile,
  isTokenizedFlow,
}) => {
  const intl = useIntl();
  const [selectedFactor, setSelectedFactor] = React.useState<Factor>();
  const errorController = useErrorController();

  const isForcedViaPhone = !isTokenizedFlow;

  const { getProfileIsLoading } = React.useContext(ProfileContext);

  const {
    isLoading: isFactorLoading,
    enrolledFactor,
    isUserInMFAGroup,
    loadIsUserInMFAGroup,
    factorList,
    handleSkip,
    activateUserFactor,
    loadFactorList,
    resendUserFactorCode,
    handleFactorSetup,
    resetActivatedFactor,
  } = React.useContext(MfaSetupContext);

  const setEmailOnlyFactor = async () => {
    const emailFactor = factorList?.find(
      (factorItem) => factorItem.factorType === FACTORS_TO_SHOW.email
    );
    if (emailFactor) {
      setSelectedFactor(emailFactor);
    }
  };

  useEffect(() => {
    loadFactorList();
    loadIsUserInMFAGroup();

    return () => resetActivatedFactor();
  }, []);

  useEffect(() => {
    if (!isTokenizedFlow) return;
    if (!factorList?.length) return;
    if (selectedFactor) return;

    setEmailOnlyFactor();
  }, [isTokenizedFlow, factorList, selectedFactor]);

  const setupFactor: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    handleFactorSetup(selectedFactor, false, isUserInMFAGroup);
  };

  const handleCode = async (code: string) => {
    try {
      await activateUserFactor(code, HeapEventLocation.accountCreation);
    } catch (error) {
      setCodeVerificationErrors(errorController, intl);
    }
  };

  const prepareFactorsObject = () => {
    if (!factorList) return {};
    const factorObj: FactorsObject = {};
    factorList.forEach((factor) => (factorObj[factor.factorType] = factor));
    return factorObj;
  };

  const handleGoBack = () => {
    resetActivatedFactor();
    setSelectedFactor(undefined);
  };

  const isSubmitActive = !!selectedFactor;

  const FactorDescription = () => {
    return (
      <div className="factor-switch__information">
        <div>
          <p>
            {intl.formatMessage(
              isTokenizedFlow
                ? messages.factorListTokenizedDescription
                : messages.factorListFirstSetupDescription
            )}
          </p>
        </div>
      </div>
    );
  };

  return (
    <>
      <SpinnerView isLoading={isFactorLoading} />
      {enrolledFactor ? (
        <ErrorProvider controller={errorController}>
          <VerifyCodeFormStepper
            type={enrolledFactor.factorType}
            profile={enrolledFactor.profile}
            handleCode={handleCode}
            handleResend={resendUserFactorCode}
            goBack={handleGoBack}
          />
        </ErrorProvider>
      ) : (
        <div className="factor-container sign-up-flow">
          <FactorDescription />

          <form onSubmit={setupFactor}>
            <FactorList
              profile={profile}
              factorsObject={prepareFactorsObject()}
              displayEmail={!isForcedViaPhone}
              setSelectedFactor={setSelectedFactor}
              selectedFactorType={selectedFactor?.factorType}
            />

            <div className="factor-footer">
              <Button unelevated type="submit" disabled={!isSubmitActive}>
                {isTokenizedFlow
                  ? intl.formatMessage(messages.factorListSave)
                  : intl.formatMessage(messages.factorListNext)}
              </Button>
              {isTokenizedFlow && (
                <Button
                  onClick={handleSkip}
                  disabled={getProfileIsLoading}
                  type="button"
                  outlined
                >
                  {intl.formatMessage(messages.factorListSkip)}
                </Button>
              )}
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default FactorContainerSignUp;
